/**
 * @file The words for each `LedgerStatementProblem` a statement proposal reports. Consumers:
 * `LedgerStatementProposalReview` in react, which colours the label as a warning or an error by
 * `LEDGER_STATEMENT_WARNINGS`, and `ledgerStatementRefusalMessage` in functions, which lists a `creditLines`
 * refusal's blocking problems with it. The switch is exhaustive, so a new problem kind fails to compile here
 * until it has words. Row numbers are sheet rows, and a dimension is named by its label when the
 * definition declares it. Money is shown in the ledger currency through `ledgerMoney`. A 0.00 row is
 * dropped with a warning. An invalid total or original statement total says why: its cell is not an
 * amount, or the amount it read is not positive (summed totals carry no cell), and a statement netting to a
 * credit is not a payment. A `totalMismatch` within the rounding allowance says the gap comes from rounding
 * rather than reading as an error.
 */
import { ledgerMoney } from './ledgerMoney';
import type { LedgerDimension } from '../System.Ledger.Definition.types';
import type {
  LedgerStatementCell,
  LedgerStatementProblem,
  LedgerStatementValueRole,
} from '../System.Ledger.types';

const ROLE_WORDS: Record<LedgerStatementValueRole, string> = {
  total: 'total',
  reference: 'reference',
  receivedAt: 'payment date',
  originalTotal: 'original statement total',
};

const cellText = (value: LedgerStatementCell): string =>
  value === null ? '' : String(value);

export const ledgerStatementProblemLabel = (
  problem: LedgerStatementProblem,
  context: { currency: string; dimensions: LedgerDimension[] },
): string => {
  switch (problem.kind) {
    case 'unreadableFile':
      return problem.message;
    case 'sheetNotFound':
      return `No sheet is named "${problem.sheet}".`;
    case 'columnNotFound':
      return `No column is headed "${problem.header}".`;
    case 'ambiguousColumn':
      return `More than one column is headed "${problem.header}".`;
    case 'unmappedColumn':
      return `Column "${problem.header}" is not mapped and was ignored.`;
    case 'rowSkipped':
      return `Row ${problem.rowNumber} was skipped by skip rule ${problem.skipRowIndex + 1}.`;
    case 'invalidAmount':
      return `Row ${problem.rowNumber}: "${cellText(problem.value)}" is not an amount.`;
    case 'zeroAmount':
      return `Row ${problem.rowNumber} carries no amount and is dropped.`;
    case 'invalidDimension': {
      const dimension = context.dimensions.find(
        (declared) => declared.dimensionId === problem.dimensionId,
      );
      return `Row ${problem.rowNumber}: "${cellText(problem.value)}" is not a valid ${dimension?.label ?? problem.dimensionId}.`;
    }
    case 'valueNotFound':
      return `The ${ROLE_WORDS[problem.role]} was not found.`;
    case 'ambiguousValue':
      return `The ${ROLE_WORDS[problem.role]} reads more than one value: ${problem.values.join(', ')}.`;
    case 'invalidValue': {
      if (problem.role === 'receivedAt')
        return `The payment date "${cellText(problem.value)}" is not a date.`;
      const subject =
        problem.role === 'total' ? 'statement total' : ROLE_WORDS[problem.role];
      if (problem.amountMinor === null)
        return `The ${subject} "${cellText(problem.value)}" is not an amount.`;
      if (problem.role === 'originalTotal')
        return `The ${subject} "${cellText(problem.value)}" is not a positive amount.`;
      return problem.value === null
        ? 'The statement total is not positive; a statement that nets to a credit is not a payment.'
        : `The statement total "${cellText(problem.value)}" is not a positive amount; a statement that nets to a credit is not a payment.`;
    }
    case 'tooManyLines':
      return `The statement has ${problem.lines} lines; at most ${problem.max} can post.`;
    case 'totalMismatch': {
      const total = ledgerMoney(problem.totalMinor, context.currency);
      const lines = ledgerMoney(problem.linesMinor, context.currency);
      return problem.withinRounding
        ? `The lines add up to ${lines} and the statement total is ${total}. The ${ledgerMoney(Math.abs(problem.totalMinor - problem.linesMinor), context.currency)} difference comes from rounding, so the statement imports.`
        : `The statement total ${total} differs from the lines' sum ${lines}.`;
    }
  }
};
